<template>
  <div class="partners-api-docs" :class="{ 'dark-theme': isDarkMode }">
    <v-container fluid>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card :dark="isDarkMode">
            <v-toolbar :color="isDarkMode ? 'dark' : 'primary'" dark>
              <v-toolbar-title>{{ $t('partnersApiDocumentation') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              
              <!-- Theme Toggle -->
              <v-btn icon @click="toggleTheme" class="mr-2">
                <v-icon>{{ isDarkMode ? 'wb_sunny' : 'brightness_2' }}</v-icon>
              </v-btn>
              
              <!-- Language Toggle -->
              <v-btn-toggle v-model="currentLanguage" mandatory class="transparent">
                <v-btn flat value="es" small>
                  {{ $t('spanish') }}
                </v-btn>
                <v-btn flat value="en" small>
                  {{ $t('english') }}
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
            
            <v-card-text class="pa-0">
              <div v-if="loading" class="text-xs-center pa-5">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                ></v-progress-circular>
                <p class="mt-3">{{ $t('loadingDocumentation') }}</p>
              </div>
              
              <div v-else-if="error" class="text-xs-center pa-5">
                <v-icon color="error" size="64">error</v-icon>
                <p class="mt-3 error--text">{{ error }}</p>
                <v-btn color="primary" @click="fetchDocumentation">
                  {{ $t('retry') }}
                </v-btn>
              </div>
              
              <div v-else class="documentation-content" :class="{ 'dark-content': isDarkMode }">
                <div v-if="currentLanguage === 'es'" class="pa-4">
                  <h2 class="mb-4">{{ $t('apiDocumentationNotice') }}</h2>
                  <p class="body-1 mb-4">{{ $t('apiDocumentationDescription') }}</p>
                  <v-alert type="info" value="true" class="mb-4">
                    {{ $t('documentationLanguageNote') }}
                  </v-alert>
                </div>
                
                <div 
                  class="api-docs-content pa-4" 
                  v-html="htmlContent"
                  :class="{ 
                    'spanish-intro': currentLanguage === 'es',
                    'dark-content': isDarkMode 
                  }"
                ></div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import Docs from '@/client/docs'

export default {
  name: 'PartnersApiDocs',
  
  data () {
    return {
      htmlContent: '',
      loading: true,
      error: null,
      currentLanguage: 'en',
      isDarkMode: false
    }
  },
  
  watch: {
    currentLanguage () {
      // When language changes, we could fetch different content
      // For now, the API only returns English content
      this.fetchDocumentation()
    }
  },
  
  async mounted () {
    // Load saved theme preference for docs page only
    const savedTheme = localStorage.getItem('docs-theme')
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'dark'
    }
    await this.fetchDocumentation()
  },
  
  methods: {
    async fetchDocumentation () {
      this.loading = true
      this.error = null
      
      try {
        const response = await Docs.getPartnersApiDoc()
        this.htmlContent = response.html
      } catch (error) {
        console.error('Error fetching API documentation:', error)
        this.error = this.$t('errorLoadingDocumentation')
      } finally {
        this.loading = false
      }
    },
    
    toggleTheme () {
      this.isDarkMode = !this.isDarkMode
      // Save theme preference for docs page only
      localStorage.setItem('docs-theme', this.isDarkMode ? 'dark' : 'light')
    }
  }
}
</script>

<style scoped>
.partners-api-docs {
  min-height: 100vh;
  background-color: #f5f5f5;
  transition: background-color 0.3s ease;
}

.partners-api-docs.dark-theme {
  background-color: #121212;
}

.documentation-content {
  background: white;
  transition: background-color 0.3s ease;
}

.documentation-content.dark-content {
  background: #1e1e1e;
}

.spanish-intro {
  border-top: 1px solid #e0e0e0;
}

.dark-content .spanish-intro {
  border-top-color: #404040;
}
</style>

<style>
/* Global styles for API documentation content */
.api-docs-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  color: #333;
}

.api-docs-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1976d2;
  margin: 2rem 0 1.5rem 0;
  border-bottom: 3px solid #1976d2;
  padding-bottom: 0.5rem;
}

.api-docs-content h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #1976d2;
  margin: 2rem 0 1rem 0;
  border-bottom: 2px solid #e3f2fd;
  padding-bottom: 0.5rem;
}

.api-docs-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1565c0;
  margin: 1.5rem 0 1rem 0;
}

.api-docs-content h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1565c0;
  margin: 1rem 0 0.75rem 0;
}

.api-docs-content p {
  margin: 1rem 0;
  color: #555;
}

.api-docs-content ul,
.api-docs-content ol {
  margin: 1rem 0;
  padding-left: 2rem;
}

.api-docs-content li {
  margin: 0.5rem 0;
  color: #555;
}

.api-docs-content strong {
  font-weight: 600;
  color: #333;
}

.api-docs-content code {
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
  font-size: 0.875rem;
  color: #d73a49;
  border: 1px solid #e1e4e8;
  width: 100%;
  display: inline-block;
  word-wrap: break-word;
}

.api-docs-content pre {
  background: #f8f9fa;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 1rem;
  overflow-x: auto;
  margin: 1.5rem 0;
  font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
  width: 100%;
}

.api-docs-content pre code {
  background: none;
  padding: 0;
  border: none;
  color: #24292e;
  font-size: 0.85rem;
}

.api-docs-content table {
  border-collapse: collapse;
  width: 100%;
  margin: 1.5rem 0;
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  overflow: hidden;
}

.api-docs-content th,
.api-docs-content td {
  border: 1px solid #e1e4e8;
  padding: 0.75rem 1rem;
  text-align: left;
}

.api-docs-content th {
  background: #f6f8fa;
  font-weight: 600;
  color: #24292e;
}

.api-docs-content tr:nth-child(even) {
  background: #f9f9f9;
}

.api-docs-content blockquote {
  border-left: 4px solid #1976d2;
  background: #e3f2fd;
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  border-radius: 0 6px 6px 0;
}

.api-docs-content blockquote p {
  margin: 0;
  color: #1565c0;
}

.api-docs-content hr {
  border: none;
  border-top: 2px solid #e1e4e8;
  margin: 2rem 0;
}

.api-docs-content a {
  color: #1976d2;
  text-decoration: none;
}

.api-docs-content a:hover {
  text-decoration: underline;
}

/* Endpoint styling */
.api-docs-content h3:contains("Endpoint") + p code,
.api-docs-content p:contains("POST ") code,
.api-docs-content p:contains("GET ") code {
  background: #2e7d32;
  color: white;
  padding: 0.5rem 1rem;
  font-weight: 600;
  border-radius: 4px;
}

/* Status code styling */
.api-docs-content li:contains("200") code,
.api-docs-content li:contains("201") code {
  background: #2e7d32;
  color: white;
}

.api-docs-content li:contains("400") code,
.api-docs-content li:contains("401") code,
.api-docs-content li:contains("404") code,
.api-docs-content li:contains("422") code {
  background: #d32f2f;
  color: white;
}

/* JSON syntax highlighting */
.api-docs-content pre:contains("{") {
  border-left: 4px solid #4caf50;
}

/* Dark theme styles */
.dark-content .api-docs-content {
  color: #e1e1e1;
}

.dark-content .api-docs-content h1 {
  color: #64b5f6;
  border-bottom-color: #64b5f6;
}

.dark-content .api-docs-content h2 {
  color: #64b5f6;
  border-bottom-color: #42a5f5;
}

.dark-content .api-docs-content h3,
.dark-content .api-docs-content h4 {
  color: #42a5f5;
}

.dark-content .api-docs-content p,
.dark-content .api-docs-content li {
  color: #b0b0b0;
}

.dark-content .api-docs-content strong {
  color: #e1e1e1;
}

.dark-content .api-docs-content code {
  background: #2d2d2d;
  color: #ff6b6b;
  border-color: #404040;
}

.dark-content .api-docs-content pre {
  background: #1e1e1e;
  border-color: #404040;
}

.dark-content .api-docs-content pre code {
  background: none;
  color: #e1e1e1;
}

.dark-content .api-docs-content table {
  background: #1e1e1e;
  border-color: #404040;
}

.dark-content .api-docs-content th,
.dark-content .api-docs-content td {
  border-color: #404040;
  color: #e1e1e1;
}

.dark-content .api-docs-content th {
  background: #2d2d2d;
  color: #e1e1e1;
}

.dark-content .api-docs-content tr:nth-child(even) {
  background: #252525;
}

.dark-content .api-docs-content blockquote {
  border-left-color: #64b5f6;
  background: #1a2332;
}

.dark-content .api-docs-content blockquote p {
  color: #64b5f6;
}

.dark-content .api-docs-content hr {
  border-top-color: #404040;
}

.dark-content .api-docs-content a {
  color: #64b5f6;
}

/* Make tables responsive */
@media (max-width: 768px) {
  .api-docs-content table {
    font-size: 0.875rem;
  }
  
  .api-docs-content th,
  .api-docs-content td {
    padding: 0.5rem;
  }
  
  .api-docs-content pre {
    font-size: 0.75rem;
    padding: 0.75rem;
  }
}
</style>